import { useGameState } from "app/game/useGame";
import Keyboard from "app/spelling-input/Keyboard";
import SpellingInput, {
  type SpellingInputHandle,
} from "app/spelling-input/SpellingInput";
import cn from "app/utils/classnames";
import useWindowSize, { WindowSize } from "app/utils/useWindowSize";
import { useCallback, useEffect, useRef, useState } from "react";

export interface KeyboardGroupHandle {
  forceInputFocus: () => void;
}

interface KeyboardGroupProps {
  onSubmit?: (text: string) => void;

  // Number of pixels. If the screen is shorter than this, the keyboard keys
  // will shrink to create more room.
  heightShrinkCutoff?: number;

  // Turns off the input element
  hideInput?: boolean;

  // Prevents keyboard input from being accepted by both the keyboard and input
  // components.
  disableInput?: boolean;

  className?: string;
}

// This groups up a spelling input and onscreen keyboard element and
// coordinates them in a way that's reusable for most round types.
export default function KeyboardGroup({
  onSubmit,
  heightShrinkCutoff = 700,
  hideInput = false,
  className = "",
  disableInput = false,
}: KeyboardGroupProps) {
  const [inputText, setInputText] = useState("");
  const inputRef = useRef<SpellingInputHandle>(null);

  const { windowHeight } = useWindowSize(undefined, heightShrinkCutoff);

  const { inputType } = useGameState();
  const [hideKeyboard, setHideKeyboard] = useState(false);
  if (hideKeyboard === false && inputType === "mouse") {
    setHideKeyboard(true);
  } else if (hideKeyboard === true && inputType === "touch") {
    setHideKeyboard(false);
  }

  useEffect(() => {
    if (hideInput === false) {
      inputRef.current?.forceFocus();
    }
  }, [hideInput]);

  const submit = useCallback(() => {
    // Don't submit if no text has been entered
    if (inputText.length === 0) {
      return;
    }
    onSubmit?.(inputText);
    inputRef.current?.clearText();
  }, [onSubmit, inputText]);

  function handleKeyboardLetterDown(letter: string) {
    inputRef.current?.insertText(letter);
  }

  function handleKeyboardBackspaceDown() {
    inputRef.current?.backspace();
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === "Return") {
      submit();
    }
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <SpellingInput
        ref={inputRef}
        className={cn("h-10", hideInput && "hidden")}
        onKeyDown={handleInputKeyDown}
        onInputChange={(newText: string) => setInputText(newText)}
        disabled={disableInput}
      />
      {!hideKeyboard && (
        <Keyboard
          onLetterDown={handleKeyboardLetterDown}
          onBackspaceDown={handleKeyboardBackspaceDown}
          onEnterDown={submit}
          useShortVersion={windowHeight === WindowSize.SMALL}
          disableInput={disableInput}
        />
      )}
    </div>
  );
}
